import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ChevronUp, ChevronDown, TrendingUp, Search, AlertCircle, Sparkles, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MarketCompetition = ({
 showMarketCompetition,
 setShowMarketCompetition,
 formData,
 productionStats,
 competitionData,
 competitionLoading,
 onUpdateAnalysis,
 outputs,
 marginStrategies
}) => {

 // Calculate sales projections based on competition difficulty
 const calculateSalesProjections = () => {
 if (!competitionData || !productionStats || !outputs) return null;

 const difficulty = competitionData.difficulty_index;
 
 // Smart capacity calculation based on competition difficulty
 // If difficulty < 50% (low competition) → higher capacity usage
 // If difficulty > 50% (high competition) → lower capacity usage
 
 let pessimisticMultiplier, expectedMultiplier, optimisticMultiplier;
 
 if (difficulty < 50) {
 // Low competition market - higher capacity usage
 pessimisticMultiplier = 0.45; // 45%
 expectedMultiplier = 0.65; // 65%
 optimisticMultiplier = 0.90; // 90%
 } else {
 // High competition market - lower capacity usage
 const competitionFactor = (difficulty - 50) / 50; // 0 to 1 scale
 pessimisticMultiplier = Math.max(0.20, 0.45 - (competitionFactor * 0.15)); // 45% down to 30%
 expectedMultiplier = Math.max(0.50, 0.65 - (competitionFactor * 0.15)); // 65% down to 50%
 optimisticMultiplier = Math.max(0.80, 0.90 - (competitionFactor * 0.10)); // 90% down to 80%
 }

 // Calculate proper breakeven units accounting for fixed and variable costs
 const pricePerUnit = parseFloat(formData.selling_price) || 0;
 const cogsPerUnit = parseFloat(outputs.total_direct_cost) / parseFloat(formData.units_per_batch || 1);
 const opexPerBatch = parseFloat(outputs.overhead_per_batch) || 0;
 const capexPerBatch = parseFloat(outputs.capex_per_batch) || 0;
 const unitsPerBatch = parseFloat(formData.units_per_batch) || 1;
 const batchesPerMonth = parseFloat(formData.batches_per_month) || 1;
 
 // Fixed costs per month (OpEx + CapEx for all batches)
 const fixedCostsPerMonth = (opexPerBatch + capexPerBatch) * batchesPerMonth;
 
 // Contribution margin per unit (Price - Variable Cost)
 const contributionMargin = pricePerUnit - cogsPerUnit;
 
 // Breakeven units = Fixed Costs / Contribution Margin
 const breakEvenUnits = contributionMargin > 0 ? Math.ceil(fixedCostsPerMonth / contributionMargin) : 0;

 return {
 breakeven: {
 unitsPerMonth: breakEvenUnits,
 revenuePerMonth: (breakEvenUnits * pricePerUnit).toFixed(0),
 percentage: ((breakEvenUnits / productionStats.unitsPerMonth) * 100).toFixed(0)
 },
 pessimistic: {
 unitsPerMonth: Math.floor(productionStats.unitsPerMonth * pessimisticMultiplier),
 revenuePerMonth: (productionStats.revenuePerMonth * pessimisticMultiplier).toFixed(0),
 percentage: (pessimisticMultiplier * 100).toFixed(0)
 },
 expected: {
 unitsPerMonth: Math.floor(productionStats.unitsPerMonth * expectedMultiplier),
 revenuePerMonth: (productionStats.revenuePerMonth * expectedMultiplier).toFixed(0),
 percentage: (expectedMultiplier * 100).toFixed(0)
 },
 optimistic: {
 unitsPerMonth: Math.floor(productionStats.unitsPerMonth * optimisticMultiplier),
 revenuePerMonth: (productionStats.revenuePerMonth * optimisticMultiplier).toFixed(0),
 percentage: (optimisticMultiplier * 100).toFixed(0)
 }
 };
 };

 // Calculate margin strategy impact on revenue
 const calculateMarginStrategyImpact = () => {
 if (!competitionData || !productionStats || !outputs || !marginStrategies) return null;
 
 const difficulty = competitionData.difficulty_index;
 const currentPrice = parseFloat(formData.selling_price) || 0;
 const unitsPerBatch = parseFloat(formData.units_per_batch) || 0;
 const batchesPerMonth = parseFloat(formData.batches_per_month) || 0;
 
 // Price elasticity: Higher prices reduce sales volume, lower prices increase it
 const calculateAdjustedSales = (newPrice, baseMultiplier) => {
 const priceRatio = newPrice / currentPrice;
 // Price elasticity factor: -0.5 (for every 10% price increase, 5% demand decrease)
 const elasticityAdjustment = Math.pow(priceRatio, -0.5);
 return baseMultiplier * elasticityAdjustment;
 };
 
 // Get multipliers for expected scenario (most realistic)
 const expectedMultiplier = difficulty > 70 ? 0.50 : difficulty > 50 ? 0.70 : 0.85;
 
 // Calculate for each margin strategy
 const strategies = {};
 const costPerUnit = parseFloat(outputs.cost_per_unit);
 const cogsPerUnit = parseFloat(outputs.total_direct_cost) / unitsPerBatch;
 const opexPerBatch = parseFloat(outputs.overhead_per_batch) || 0;
 const capexPerBatch = parseFloat(outputs.capex_per_batch) || 0;
 const fixedCostsPerMonth = (opexPerBatch + capexPerBatch) * batchesPerMonth;
 
 // Breakeven with current price
 const contributionMargin = currentPrice - cogsPerUnit;
 const breakEvenUnits = contributionMargin > 0 ? Math.ceil(fixedCostsPerMonth / contributionMargin) : 0;
 const breakEvenBatches = Math.ceil(breakEvenUnits / unitsPerBatch);
 const breakEvenProduction = breakEvenBatches * unitsPerBatch;
 
 strategies.breakeven = {
 margin: 'Breakeven',
 price: currentPrice.toFixed(2),
 unitsPerMonth: breakEvenUnits,
 revenuePerMonth: (breakEvenUnits * currentPrice).toFixed(0),
 cogsPerMonth: (breakEvenUnits * cogsPerUnit).toFixed(0),
 netProfitPerMonth: '0',
 batchesNeeded: breakEvenBatches,
 totalProduction: breakEvenProduction,
 carryover: breakEvenProduction - breakEvenUnits,
 recommendedFor: 'Minimum to Cover All Costs'
 };
 
 if (marginStrategies.margin25) {
 const price25 = parseFloat(marginStrategies.margin25.requiredPrice);
 const adjustedMultiplier = calculateAdjustedSales(price25, expectedMultiplier);
 const unitsPerMonth = Math.floor(unitsPerBatch * batchesPerMonth * adjustedMultiplier);
 const batchesNeeded = Math.ceil(unitsPerMonth / unitsPerBatch);
 const totalProduction = batchesNeeded * unitsPerBatch;
 const carryover = totalProduction - unitsPerMonth;
 
 strategies.margin25 = {
 margin: '25%',
 price: price25.toFixed(2),
 unitsPerMonth: unitsPerMonth,
 revenuePerMonth: (unitsPerMonth * price25).toFixed(0),
 cogsPerMonth: (unitsPerMonth * costPerUnit).toFixed(0),
 netProfitPerMonth: (unitsPerMonth * (price25 - costPerUnit)).toFixed(0),
 batchesNeeded: batchesNeeded,
 totalProduction: totalProduction,
 carryover: carryover,
 recommendedFor: 'High Competition (Build Market Share)'
 };
 }
 
 if (marginStrategies.margin45) {
 const price45 = parseFloat(marginStrategies.margin45.requiredPrice);
 const adjustedMultiplier = calculateAdjustedSales(price45, expectedMultiplier);
 const unitsPerMonth = Math.floor(unitsPerBatch * batchesPerMonth * adjustedMultiplier);
 const batchesNeeded = Math.ceil(unitsPerMonth / unitsPerBatch);
 const totalProduction = batchesNeeded * unitsPerBatch;
 const carryover = totalProduction - unitsPerMonth;
 
 strategies.margin45 = {
 margin: '45%',
 price: price45.toFixed(2),
 unitsPerMonth: unitsPerMonth,
 revenuePerMonth: (unitsPerMonth * price45).toFixed(0),
 cogsPerMonth: (unitsPerMonth * costPerUnit).toFixed(0),
 netProfitPerMonth: (unitsPerMonth * (price45 - costPerUnit)).toFixed(0),
 batchesNeeded: batchesNeeded,
 totalProduction: totalProduction,
 carryover: carryover,
 recommendedFor: 'Moderate Competition (Balanced Growth)'
 };
 }
 
 if (marginStrategies.margin65) {
 const price65 = parseFloat(marginStrategies.margin65.requiredPrice);
 const adjustedMultiplier = calculateAdjustedSales(price65, expectedMultiplier);
 const unitsPerMonth = Math.floor(unitsPerBatch * batchesPerMonth * adjustedMultiplier);
 const batchesNeeded = Math.ceil(unitsPerMonth / unitsPerBatch);
 const totalProduction = batchesNeeded * unitsPerBatch;
 const carryover = totalProduction - unitsPerMonth;
 
 strategies.margin65 = {
 margin: '65%',
 price: price65.toFixed(2),
 unitsPerMonth: unitsPerMonth,
 revenuePerMonth: (unitsPerMonth * price65).toFixed(0),
 cogsPerMonth: (unitsPerMonth * costPerUnit).toFixed(0),
 netProfitPerMonth: (unitsPerMonth * (price65 - costPerUnit)).toFixed(0),
 batchesNeeded: batchesNeeded,
 totalProduction: totalProduction,
 carryover: carryover,
 recommendedFor: 'Low Competition (Premium Positioning)'
 };
 }
 
 return strategies;
 };

 // Calculate anticipated production requirements for sales scenarios
 const calculateProductionRequirements = () => {
 if (!competitionData || !productionStats || !formData.cycle_time_hours) return null;
 
 const unitsPerBatch = parseFloat(formData.units_per_batch) || 0;
 const cycleTimeHours = parseFloat(formData.cycle_time_hours) || 0;
 
 // Get sales projections
 const projections = calculateSalesProjections();
 if (!projections) return null;
 
 const calculateScenarioRequirements = (unitsNeeded) => {
 const batchesNeeded = Math.ceil(unitsNeeded / unitsPerBatch);
 
 // Calculate production days for different work schedules
 const productionDays = {
 schedule8hr: Math.ceil((batchesNeeded * cycleTimeHours) / 8),
 schedule12hr: Math.ceil((batchesNeeded * cycleTimeHours) / 12),
 schedule16hr: Math.ceil((batchesNeeded * cycleTimeHours) / 16),
 schedule24hr: Math.ceil((batchesNeeded * cycleTimeHours) / 24)
 };
 
 return {
 batchesNeeded,
 productionDays
 };
 };
 
 return {
 breakeven: calculateScenarioRequirements(projections.breakeven.unitsPerMonth),
 pessimistic: calculateScenarioRequirements(projections.pessimistic.unitsPerMonth),
 expected: calculateScenarioRequirements(projections.expected.unitsPerMonth),
 optimistic: calculateScenarioRequirements(projections.optimistic.unitsPerMonth)
 };
 };

 const projections = calculateSalesProjections();
 const marginImpact = calculateMarginStrategyImpact();
 const productionRequirements = calculateProductionRequirements();

 return (
 <Card className="border-2 border-primary/30 dark:border-primary/30">
 <CardHeader>
 <div className="flex items-center justify-between">
 <div className="flex-1 cursor-pointer" onClick={() => setShowMarketCompetition(!showMarketCompetition)}>
 <CardTitle className="flex items-center gap-2">
 <Search className="w-5 h-5 text-primary" />
 Estimated Market Competition Analysis
 </CardTitle>
 <CardDescription>AI-powered local market competition assessment</CardDescription>
 </div>
 <div className="flex items-center gap-2">
 {competitionData && (
 <Button
 variant="outline"
 size="sm"
 onClick={onUpdateAnalysis}
 disabled={competitionLoading}
 className="flex items-center gap-2"
 >
 <RefreshCw className={`w-4 h-4 ${competitionLoading ? 'animate-spin' : ''}`} />
 {competitionLoading ? 'Updating...' : 'Update Analysis'}
 </Button>
 )}
 <button onClick={() => setShowMarketCompetition(!showMarketCompetition)} className="p-1">
 {showMarketCompetition ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
 </button>
 </div>
 </div>
 </CardHeader>
 {showMarketCompetition && (
 <CardContent className="space-y-4">
 {/* Disclaimer */}
 <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-300 dark:border-amber-700">
 <div className="flex gap-2">
 <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
 <div>
 <p className="text-xs font-semibold text-amber-900 dark:text-amber-100 mb-1">
 Estimated Analysis Only
 </p>
 <p className="text-xs text-amber-800 dark:text-amber-200">
 This AI-generated competition analysis is an <strong>estimate</strong> and should not replace thorough local market research. 
 You must conduct your own in-depth market analysis, competitor research, and customer validation before launching your business.
 </p>
 </div>
 </div>
 </div>

 {!competitionData ? (
 <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded text-center">
 <p className="text-sm text-muted-foreground">
 Enter Product Type and Location in Batch Basics, then click "Calculate Results" to see market analysis
 </p>
 </div>
 ) : (
 <>
 {/* Competition Difficulty Index */}
 <div className="border border-primary/30 dark:border-primary/30 rounded-lg overflow-hidden">
 <div className="bg-pro-soft dark:/30 dark:to-indigo-900/30 px-4 py-3 border-b border-primary/30 dark:border-primary/30">
 <h3 className="text-sm font-bold text-primary dark:text-primary">Competition Difficulty Index</h3>
 <p className="text-xs text-primary dark:text-primary">Based on local market analysis</p>
 </div>
 <div className="p-4">
 <div className="flex items-center justify-between mb-2">
 <span className="text-sm font-medium">Difficulty Level:</span>
 <span className={`text-2xl font-bold ${
 competitionData.difficulty_index > 70 ? 'text-red-600' : 
 competitionData.difficulty_index > 40 ? 'text-amber-600' : 
 'text-green-600'
 }`}>
 {competitionData.difficulty_index}%
 </span>
 </div>
 <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3 mb-3">
 <div 
 className={`h-3 rounded-full ${
 competitionData.difficulty_index > 70 ? 'bg-red-500' : 
 competitionData.difficulty_index > 40 ? 'bg-amber-500' : 
 'bg-green-500'
 }`}
 style={{ width: `${competitionData.difficulty_index}%` }}
 ></div>
 </div>
 <p className="text-xs text-muted-foreground">{typeof competitionData.analysis === 'string' ? competitionData.analysis : ''}</p>
 </div>
 </div>

 {/* Realistic Sales Projections */}
 {projections && (
 <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
 <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 px-4 py-2 border-b border-slate-200 dark:border-slate-700">
 <p className="text-sm font-bold text-slate-900 dark:text-slate-100">Realistic Sales Volume Projections</p>
 <p className="text-xs text-slate-600 dark:text-slate-400">Based on competition difficulty & production capacity</p>
 </div>
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
 <th className="px-4 py-2 text-left font-semibold">Scenario</th>
 <th className="px-4 py-2 text-center font-semibold">Capacity Used</th>
 <th className="px-4 py-2 text-center font-semibold">Units/Month</th>
 <th className="px-4 py-2 text-center font-semibold">Revenue/Month</th>
 </tr>
 </thead>
 <tbody>
 {/* Breakeven */}
 {projections.breakeven && (
 <tr className="border-b-2 border-slate-400 dark:border-slate-600 bg-slate-100 dark:bg-slate-800">
 <td className="px-4 py-3">
 <div>
 <p className="font-semibold text-slate-900 dark:text-slate-100">Breakeven</p>
 <p className="text-xs text-slate-600 dark:text-slate-400">Minimum to cover costs</p>
 </div>
 </td>
 <td className="px-4 py-3 text-center font-medium">{projections.breakeven.percentage}%</td>
 <td className="px-4 py-3 text-center font-medium">{projections.breakeven.unitsPerMonth.toLocaleString()}</td>
 <td className="px-4 py-3 text-center font-bold text-slate-700 dark:text-slate-300">
 ${Number(projections.breakeven.revenuePerMonth).toLocaleString()}
 </td>
 </tr>
 )}

 {/* Pessimistic */}
 <tr className="border-b border-slate-200 dark:border-slate-700 bg-red-50 dark:bg-red-900/10">
 <td className="px-4 py-3">
 <div>
 <p className="font-semibold text-red-700 dark:text-red-300">Pessimistic</p>
 <p className="text-xs text-red-600 dark:text-red-400">Difficult market entry</p>
 </div>
 </td>
 <td className="px-4 py-3 text-center font-medium">{projections.pessimistic.percentage}%</td>
 <td className="px-4 py-3 text-center font-medium">{projections.pessimistic.unitsPerMonth.toLocaleString()}</td>
 <td className="px-4 py-3 text-center font-bold text-red-700 dark:text-red-300">
 ${Number(projections.pessimistic.revenuePerMonth).toLocaleString()}
 </td>
 </tr>

 {/* Expected */}
 <tr className="border-b border-slate-200 dark:border-slate-700 bg-blue-50 dark:bg-blue-900/10">
 <td className="px-4 py-3">
 <div>
 <p className="font-semibold text-blue-700 dark:text-blue-300">Expected</p>
 <p className="text-xs text-blue-600 dark:text-blue-400">Most likely outcome</p>
 </div>
 </td>
 <td className="px-4 py-3 text-center font-medium">{projections.expected.percentage}%</td>
 <td className="px-4 py-3 text-center font-medium">{projections.expected.unitsPerMonth.toLocaleString()}</td>
 <td className="px-4 py-3 text-center font-bold text-blue-700 dark:text-blue-300">
 ${Number(projections.expected.revenuePerMonth).toLocaleString()}
 </td>
 </tr>

 {/* Optimistic */}
 <tr className="border-b-2 border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10">
 <td className="px-4 py-3">
 <div>
 <p className="font-semibold text-green-700 dark:text-green-300">Optimistic</p>
 <p className="text-xs text-green-600 dark:text-green-400">Favorable conditions</p>
 </div>
 </td>
 <td className="px-4 py-3 text-center font-medium">{projections.optimistic.percentage}%</td>
 <td className="px-4 py-3 text-center font-medium">{projections.optimistic.unitsPerMonth.toLocaleString()}</td>
 <td className="px-4 py-3 text-center font-bold text-green-700 dark:text-green-300">
 ${Number(projections.optimistic.revenuePerMonth).toLocaleString()}
 </td>
 </tr>
 </tbody>
 </table>
 </div>
 )}

 {/* Margin Strategy Impact Analysis */}
 {marginImpact && (
 <div className="border border-amber-200 dark:border-amber-700 rounded-lg overflow-hidden">
 <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 px-4 py-3 border-b border-amber-200 dark:border-amber-700">
 <div className="flex items-center gap-2">
 <Sparkles className="w-5 h-5 text-amber-600" />
 <div>
 <p className="text-sm font-bold text-amber-900 dark:text-amber-100">Pricing Strategy Impact on Revenue</p>
 <p className="text-xs text-amber-700 dark:text-amber-300">How different margin targets affect your realistic sales & profit</p>
 </div>
 </div>
 </div>
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-slate-50 dark:bg-slate-800 border-b-2 border-slate-300 dark:border-slate-700">
 <th className="px-3 py-2 text-left font-semibold">Margin Strategy</th>
 <th className="px-3 py-2 text-center font-semibold">Price</th>
 <th className="px-3 py-2 text-center font-semibold">Units/Month</th>
 <th className="px-3 py-2 text-center font-semibold">Revenue</th>
 <th className="px-3 py-2 text-center font-semibold">COGS</th>
 <th className="px-3 py-2 text-center font-semibold">Net Profit</th>
 <th className="px-3 py-2 text-center font-semibold">Carryover</th>
 </tr>
 </thead>
 <tbody>
 {/* Breakeven */}
 {marginImpact.breakeven && (
 <tr className="border-b-2 border-slate-400 dark:border-slate-600 bg-slate-100 dark:bg-slate-800">
 <td className="px-3 py-3">
 <div>
 <p className="font-semibold text-slate-900 dark:text-slate-100">Breakeven</p>
 <p className="text-[10px] text-slate-600 dark:text-slate-400">{marginImpact.breakeven.recommendedFor}</p>
 </div>
 </td>
 <td className="px-3 py-3 text-center font-medium">${marginImpact.breakeven.price}</td>
 <td className="px-3 py-3 text-center font-medium">{marginImpact.breakeven.unitsPerMonth.toLocaleString()}</td>
 <td className="px-3 py-3 text-center font-bold text-slate-700 dark:text-slate-300">
 ${Number(marginImpact.breakeven.revenuePerMonth).toLocaleString()}
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-700 dark:text-slate-300">
 ${Number(marginImpact.breakeven.cogsPerMonth).toLocaleString()}
 </td>
 <td className="px-3 py-3 text-center font-bold text-slate-700 dark:text-slate-300">
 $0
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {marginImpact.breakeven.carryover} units
 </td>
 </tr>
 )}

 {/* 25% Margin Strategy */}
 {marginImpact.margin25 && (
 <tr className="border-b border-slate-200 dark:border-slate-700 bg-orange-50 dark:bg-orange-900/10">
 <td className="px-3 py-3">
 <div>
 <p className="font-semibold text-orange-700 dark:text-orange-300">25% Margin</p>
 <p className="text-[10px] text-orange-600 dark:text-orange-400">{marginImpact.margin25.recommendedFor}</p>
 </div>
 </td>
 <td className="px-3 py-3 text-center font-medium">${marginImpact.margin25.price}</td>
 <td className="px-3 py-3 text-center font-medium">{marginImpact.margin25.unitsPerMonth.toLocaleString()}</td>
 <td className="px-3 py-3 text-center font-bold text-blue-700 dark:text-blue-300">
 ${Number(marginImpact.margin25.revenuePerMonth).toLocaleString()}
 </td>
 <td className="px-3 py-3 text-center font-medium text-orange-700 dark:text-orange-300">
 ${Number(marginImpact.margin25.cogsPerMonth).toLocaleString()}
 </td>
 <td className="px-3 py-3 text-center font-bold text-green-700 dark:text-green-300">
 ${Number(marginImpact.margin25.netProfitPerMonth).toLocaleString()}
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {marginImpact.margin25.carryover} units
 </td>
 </tr>
 )}

 {/* 45% Margin Strategy - Recommended */}
 {marginImpact.margin45 && (
 <tr className="border-b border-slate-200 dark:border-slate-700 bg-green-50 dark:bg-green-900/10 ring-2 ring-green-400 dark:ring-green-600">
 <td className="px-3 py-3">
 <div>
 <p className="font-semibold text-green-700 dark:text-green-300">45% Margin ⭐</p>
 <p className="text-[10px] text-green-600 dark:text-green-400">{marginImpact.margin45.recommendedFor}</p>
 </div>
 </td>
 <td className="px-3 py-3 text-center font-medium">${marginImpact.margin45.price}</td>
 <td className="px-3 py-3 text-center font-medium">{marginImpact.margin45.unitsPerMonth.toLocaleString()}</td>
 <td className="px-3 py-3 text-center font-bold text-blue-700 dark:text-blue-300">
 ${Number(marginImpact.margin45.revenuePerMonth).toLocaleString()}
 </td>
 <td className="px-3 py-3 text-center font-medium text-orange-700 dark:text-orange-300">
 ${Number(marginImpact.margin45.cogsPerMonth).toLocaleString()}
 </td>
 <td className="px-3 py-3 text-center font-bold text-green-700 dark:text-green-300">
 ${Number(marginImpact.margin45.netProfitPerMonth).toLocaleString()}
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {marginImpact.margin45.carryover} units
 </td>
 </tr>
 )}

 {/* 65% Margin Strategy */}
 {marginImpact.margin65 && (
 <tr className="border-b-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10">
 <td className="px-3 py-3">
 <div>
 <p className="font-semibold text-amber-700 dark:text-amber-300">65% Margin</p>
 <p className="text-[10px] text-amber-600 dark:text-amber-400">{marginImpact.margin65.recommendedFor}</p>
 </div>
 </td>
 <td className="px-3 py-3 text-center font-medium">${marginImpact.margin65.price}</td>
 <td className="px-3 py-3 text-center font-medium">{marginImpact.margin65.unitsPerMonth.toLocaleString()}</td>
 <td className="px-3 py-3 text-center font-bold text-blue-700 dark:text-blue-300">
 ${Number(marginImpact.margin65.revenuePerMonth).toLocaleString()}
 </td>
 <td className="px-3 py-3 text-center font-medium text-orange-700 dark:text-orange-300">
 ${Number(marginImpact.margin65.cogsPerMonth).toLocaleString()}
 </td>
 <td className="px-3 py-3 text-center font-bold text-green-700 dark:text-green-300">
 ${Number(marginImpact.margin65.netProfitPerMonth).toLocaleString()}
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {marginImpact.margin65.carryover} units
 </td>
 </tr>
 )}
 </tbody>
 </table>
 <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-3 text-xs text-blue-900 dark:text-blue-100 border-t border-blue-200 dark:border-blue-700">
 <p className="font-semibold mb-1">💡 Pricing Strategy Insights:</p>
 <ul className="list-disc list-inside space-y-0.5 ml-2 text-blue-800 dark:text-blue-200">
 <li><strong>25% Margin:</strong> Lower price = higher volume, good for entering competitive markets</li>
 <li><strong>45% Margin:</strong> Balanced approach, maximizes profit while maintaining good sales volume</li>
 <li><strong>65% Margin:</strong> Premium pricing = lower volume but higher profit per unit, requires strong brand</li>
 <li>Note: Estimates include price elasticity effects (higher prices typically reduce sales volume)</li>
 </ul>
 </div>
 </div>
 )}

 {/* Anticipated Production Requirements */}
 {productionRequirements && (
 <div className="border border-indigo-200 dark:border-indigo-700 rounded-lg overflow-hidden">
 <div className="bg-gradient-to-r to-blue-50 dark:from-indigo-900/30 dark:to-blue-900/30 px-4 py-3 border-b border-indigo-200 dark:border-indigo-700">
 <div className="flex items-center gap-2">
 <TrendingUp className="w-5 h-5 text-indigo-600" />
 <div>
 <p className="text-sm font-bold text-indigo-900 dark:text-indigo-100">Anticipated Production Requirements</p>
 <p className="text-xs text-indigo-700 dark:text-indigo-300">Batches & capacity needed to meet sales scenarios</p>
 </div>
 </div>
 </div>
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-slate-50 dark:bg-slate-800 border-b-2 border-slate-300 dark:border-slate-700">
 <th className="px-3 py-2 text-left font-semibold">Sales Scenario</th>
 <th className="px-3 py-2 text-center font-semibold">Batches Needed</th>
 <th className="px-3 py-2 text-center font-semibold text-xs">8hr Days</th>
 <th className="px-3 py-2 text-center font-semibold text-xs">12hr Days</th>
 <th className="px-3 py-2 text-center font-semibold text-xs">16hr Days</th>
 <th className="px-3 py-2 text-center font-semibold text-xs">24hr Days</th>
 </tr>
 </thead>
 <tbody>
 {/* Breakeven Scenario */}
 {productionRequirements.breakeven && (
 <tr className="border-b-2 border-slate-400 dark:border-slate-600 bg-slate-100 dark:bg-slate-800">
 <td className="px-3 py-3">
 <div>
 <p className="font-semibold text-slate-900 dark:text-slate-100">Breakeven</p>
 <p className="text-xs text-slate-600 dark:text-slate-400">Minimum to cover costs</p>
 </div>
 </td>
 <td className="px-3 py-3 text-center font-medium">
 {productionRequirements.breakeven.batchesNeeded}
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {productionRequirements.breakeven.productionDays.schedule8hr}d
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {productionRequirements.breakeven.productionDays.schedule12hr}d
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {productionRequirements.breakeven.productionDays.schedule16hr}d
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {productionRequirements.breakeven.productionDays.schedule24hr}d
 </td>
 </tr>
 )}

 {/* Pessimistic Scenario */}
 <tr className="border-b border-slate-200 dark:border-slate-700 bg-red-50 dark:bg-red-900/10">
 <td className="px-3 py-3">
 <div>
 <p className="font-semibold text-red-700 dark:text-red-300">Pessimistic</p>
 <p className="text-xs text-red-600 dark:text-red-400">Difficult market entry</p>
 </div>
 </td>
 <td className="px-3 py-3 text-center font-medium">
 {productionRequirements.pessimistic.batchesNeeded}
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {productionRequirements.pessimistic.productionDays.schedule8hr}d
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {productionRequirements.pessimistic.productionDays.schedule12hr}d
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {productionRequirements.pessimistic.productionDays.schedule16hr}d
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {productionRequirements.pessimistic.productionDays.schedule24hr}d
 </td>
 </tr>

 {/* Expected Scenario */}
 <tr className="border-b border-slate-200 dark:border-slate-700 bg-blue-50 dark:bg-blue-900/10">
 <td className="px-3 py-3">
 <div>
 <p className="font-semibold text-blue-700 dark:text-blue-300">Expected</p>
 <p className="text-xs text-blue-600 dark:text-blue-400">Most likely outcome</p>
 </div>
 </td>
 <td className="px-3 py-3 text-center font-medium">
 {productionRequirements.expected.batchesNeeded}
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {productionRequirements.expected.productionDays.schedule8hr}d
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {productionRequirements.expected.productionDays.schedule12hr}d
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {productionRequirements.expected.productionDays.schedule16hr}d
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {productionRequirements.expected.productionDays.schedule24hr}d
 </td>
 </tr>

 {/* Optimistic Scenario */}
 <tr className="border-b-2 border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10">
 <td className="px-3 py-3">
 <div>
 <p className="font-semibold text-green-700 dark:text-green-300">Optimistic</p>
 <p className="text-xs text-green-600 dark:text-green-400">Favorable conditions</p>
 </div>
 </td>
 <td className="px-3 py-3 text-center font-medium">
 {productionRequirements.optimistic.batchesNeeded}
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {productionRequirements.optimistic.productionDays.schedule8hr}d
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {productionRequirements.optimistic.productionDays.schedule12hr}d
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {productionRequirements.optimistic.productionDays.schedule16hr}d
 </td>
 <td className="px-3 py-3 text-center font-medium text-slate-600 dark:text-slate-400">
 {productionRequirements.optimistic.productionDays.schedule24hr}d
 </td>
 </tr>
 </tbody>
 </table>
 <div className="bg-indigo-50 dark:bg-indigo-900/20 px-4 py-3 text-xs text-indigo-900 dark:text-indigo-100 border-t border-indigo-200 dark:border-indigo-700">
 <p className="font-semibold mb-1">📊 Production Requirements Insights:</p>
 <ul className="list-disc list-inside space-y-0.5 ml-2 text-indigo-800 dark:text-indigo-200">
 <li><strong>Production Days:</strong> Based on {parseFloat(formData.cycle_time_hours) || 0}-hour cycle time running 24/7</li>
 <li><strong>Batch Planning:</strong> Shows number of batches needed to meet each sales scenario</li>
 <li><strong>Timeline:</strong> Total days required to produce sufficient inventory</li>
 <li>Consider equipment utilization and maintenance schedules in your planning</li>
 </ul>
 </div>
 </div>
 )}

 {/* Competition Difficulty vs Revenue Chart */}
 {competitionData && (
 <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
 <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
 <p className="text-sm font-bold text-slate-900 dark:text-slate-100">Market Difficulty Impact Analysis</p>
 <p className="text-xs text-slate-600 dark:text-slate-400">How competition affects your revenue potential</p>
 </div>
 <div className="p-4 space-y-3">
 {/* Visual difficulty scale */}
 <div className="space-y-2">
 <div className="flex justify-between text-xs font-medium">
 <span className="text-green-600">Low Competition</span>
 <span className="text-amber-600">Moderate</span>
 <span className="text-red-600">High Competition</span>
 </div>
 <div className="relative h-8 bg-pro-soft from-green-400 via-amber-400 to-red-400 rounded-lg">
 <div 
 className="absolute top-0 bottom-0 w-1 bg-slate-900 dark:bg-white"
 style={{ left: `${competitionData.difficulty_index}%` }}
 >
 <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-2 py-1 rounded text-xs font-bold whitespace-nowrap">
 Your Market: {competitionData.difficulty_index}%
 </div>
 </div>
 </div>
 <div className="flex justify-between text-xs text-muted-foreground">
 <span>0%</span>
 <span>50%</span>
 <span>100%</span>
 </div>
 </div>

 {/* Impact summary */}
 <div className="grid grid-cols-3 gap-2 pt-2">
 <div className={`p-3 rounded text-center ${
 competitionData.difficulty_index < 40 ? 'bg-green-100 dark:bg-green-900/20 ring-2 ring-green-500' : 'bg-slate-100 dark:bg-slate-800'
 }`}>
 <p className="text-xs text-muted-foreground mb-1">If Low (0-40%)</p>
 <p className="text-lg font-bold text-green-600">85-95%</p>
 <p className="text-[10px] text-muted-foreground">of capacity sold</p>
 </div>
 <div className={`p-3 rounded text-center ${
 competitionData.difficulty_index >= 40 && competitionData.difficulty_index < 70 ? 'bg-amber-100 dark:bg-amber-900/20 ring-2 ring-amber-500' : 'bg-slate-100 dark:bg-slate-800'
 }`}>
 <p className="text-xs text-muted-foreground mb-1">If Moderate (40-70%)</p>
 <p className="text-lg font-bold text-amber-600">50-70%</p>
 <p className="text-[10px] text-muted-foreground">of capacity sold</p>
 </div>
 <div className={`p-3 rounded text-center ${
 competitionData.difficulty_index >= 70 ? 'bg-red-100 dark:bg-red-900/20 ring-2 ring-red-500' : 'bg-slate-100 dark:bg-slate-800'
 }`}>
 <p className="text-xs text-muted-foreground mb-1">If High (70-100%)</p>
 <p className="text-lg font-bold text-red-600">30-50%</p>
 <p className="text-[10px] text-muted-foreground">of capacity sold</p>
 </div>
 </div>
 </div>
 </div>
 )}
 </>
 )}
 </CardContent>
 )}
 </Card>
 );
};

export default MarketCompetition;
